<template>
  <transition-group :name="`image-${sliderTransition}`" mode="out-in">
    <div
      v-for="(background, index) in slides"
      v-show="currentIndex === index"
      :key="background.id"
      class="section__background-item"
      :style="{
        ...background.style
      }"
    ></div>
  </transition-group>
</template>

<script lang="ts">
import { ref, defineComponent, onMounted, PropType, onUnmounted, computed } from 'vue';
import { SliderTypes } from '@/typings/enums/enums';
import { BackgroundSlideState } from '@/components/layout/SettingsModel';
import { isSsr } from '@/utilities/Utilities';

export default defineComponent({
  name: 'BackgroundImagesSlider',

  props: {
    slides: {
      type: Array as PropType<BackgroundSlideState[]>
    },
    sliderTransition: {
      type: String as PropType<SliderTypes>
    },
    sliderTransitionDuration: {
      type: Number
    },
    sliderTransitionInterval: {
      type: Number
    },
    autoplay: {
      type: Boolean
    }
  },
  setup(props) {
    const maxIndex = ref(props?.slides?.length);
    const timer = ref<number | null>(null);
    const currentIndex = ref(0);

    const transition = computed(() => {
      return `all ${props.sliderTransitionDuration}ms ease`;
    });

    const resetCurrentIndex = () => {
      currentIndex.value = 0;
    };

    const nextSlide = () => {
      currentIndex.value++;
      if (currentIndex.value && maxIndex.value && currentIndex.value >= maxIndex.value) {
        resetCurrentIndex();
      }
    };

    const prevSlide = () => {
      currentIndex.value--;
      if (currentIndex.value && maxIndex.value && currentIndex?.value <= maxIndex?.value) {
        resetCurrentIndex();
      }
    };

    const startSlider = () => {
      if (!isSsr()) {
        timer.value = window.setInterval(nextSlide, props.sliderTransitionInterval || 3000);
        window.focus();
      }
    };
    const removeSlider = () => {
      if (timer.value) {
        window.clearInterval(timer.value);
      }
    };

    const onFocusListener = () => {
      startSlider();
    };
    const onBlurListener = () => {
      removeSlider();
    };

    onMounted(() => {
      if (!isSsr()) {
        window.addEventListener('focus', onFocusListener);
        window.addEventListener('blur', onBlurListener);
      }
      // Solution to the issue that Interval adds - when you leave the page - and CSS is paused.
      if (!timer.value) {
        startSlider();
      }
    });

    onUnmounted(() => {
      removeSlider();
      if (!isSsr()) {
        window.removeEventListener('focus', onFocusListener);
        window.removeEventListener('blur', onBlurListener);
      }
    });

    return {
      nextSlide,
      prevSlide,
      transition,
      currentIndex
    };
  }
});
</script>

<style lang="scss">
/* FADE */
.image-fade-enter-from,
.image-fade-leave-to {
  opacity: 0 !important;
}

.image-fade-leave-from,
.image-fade-enter-to {
  opacity: 1 !important;
}
.image-fade-enter-active,
.image-fade-leave-active {
  transition: v-bind('transition');
  overflow: hidden;
  visibility: visible;
  position: absolute;
  width: 100%;
  opacity: 1;
}
.image-fade-enter,
.image-fade-leave-to {
  visibility: hidden;
  width: 100%;
  opacity: 0;
}

/* SLIDE */

/* Enter and leave animations can use different */
/* durations and timing functions.              */

.image-slide-from,
.image-slide-to {
  transform: translateX(0);
}

.image-slide-leave-to {
  transform: translateX(-100%);
}
.image-slide-enter-from {
  transform: translateX(100%);
}

.image-slide-enter-active,
.image-slide-leave-active {
  transition: all 1s ease;
  overflow: hidden;
  visibility: visible;
  position: absolute;
  width: 100%;
  opacity: 1;
}

// .slide-leave-active,
// .slide-enter-active {
//   transition: 1s;
// }
// .slide-enter {
//   transform: translate(100%, 0);
// }
// .slide-leave-to {
//   transform: translate(-100%, 0);
// }

// .slide-enter-from {
//   transform: translate(100%, 0);
// }

// .slide-leave-from,
// .slide-enter-to {
//   transform: translate(0, 0);
// }
// .slide-enter-active,
// .slide-leave-active {
//   transition: all 1s ease;
//   overflow: hidden;
//   visibility: visible;
//   position: absolute;
//   width: 100%;
//   opacity: 1;
// }
// .slide-leave-to {
//   transform: translate(-100%, 0);
// }
</style>
